import React, { useRef, useState, useEffect } from 'react';
import { Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import useOutsideClickAlert from 'components/library/hook/clickOutside';

import styles from './InvestmentsFormDropdown.module.css';

interface Props {
  sharesPrice: number,
  value: number,
  setValue: (value: number) => void,
  minimum: number;
  isPEA: boolean;
}

export default function InvestmentsFormDropdown({ sharesPrice, value, setValue, minimum, isPEA }: Props) {
  const [tmpValue, setTmpValue] = useState<number>(value);
  const [isOpen, setIsOpen] = useState(false);
  const [shares, setShares] = useState<number[]>([]);
  const [focusInput, setFocusInput] = useState(false);

  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTmpValue(value);
  }, [value]);

  useEffect(() => {
    if (focusInput) {
      inputRef.current?.focus();
      setFocusInput(false);
    }
  }, [focusInput]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  useOutsideClickAlert(dropdownRef, () => setIsOpen(false));

  const setSharesValue = (investment: number | undefined) => {
    if (!investment) return;
    const minimumSharesNumber = Math.floor(minimum / sharesPrice);
    const sharesNumber = Math.floor(investment / sharesPrice);
    if (sharesNumber < minimumSharesNumber) {
      setShares([
        minimumSharesNumber * sharesPrice,
      ]);
    } else if (sharesNumber !== Number.parseFloat((investment / sharesPrice).toFixed(5))) {
      setShares([
        (sharesNumber + 1) * sharesPrice,
        sharesNumber * sharesPrice,
        (sharesNumber - 1) * sharesPrice
      ].filter((amount) => (amount > 0)));
    } else {
      if ((investment >= minimum)) {
        setValue(investment);
      }
      setTmpValue(investment);
      setShares([]);
    }
  }

  const handleNewValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTmpValue(parseFloat(e.target.value));
    setSharesValue(parseFloat(e.target.value));
    setIsOpen(true);
  }


  const verifyValue = () => {
    if (!tmpValue) setTmpValue(value);
    if (tmpValue < 0) setValue(minimum);
    if (tmpValue < minimum) setValue(minimum);
  };

  useEffect(() => {
    verifyValue();
  }, [minimum])

  const handleValueSelection = (e: React.MouseEvent<HTMLButtonElement>) => {
    const currentElement = e.target as HTMLElement;
    const investment = parseFloat(('name' in currentElement && currentElement.name) as string || '0');
    if (investment >= sharesPrice) setValue(investment);
    setTmpValue(investment);
    setShares([]);
    setIsOpen(false);
  }

  const handleFocus = () => {
    setIsOpen(true);
  }

  const handleClick = () => {
    setIsOpen(true);
    setFocusInput(true);
  }

  return (
    <div>
      <p className={styles.title}>
        {t('investments.amount.title')}
      </p>
      <p className={styles.description}>
        {t('investments.amount.description')}
        {
          (isPEA)
          && (
            <b className={`fadeIn ${styles.description}`}>
              {t('investments.pea.amount', { value: minimum.toLocaleString() })}
            </b>
          )
        }
      </p>
      <div
        className={`${styles.input} ${(!isOpen && (tmpValue !== value)) ? styles.error : ''}`}
        onClick={handleClick}
      >
        <p>€</p>
        <input
          type="number"
          value={tmpValue || ''}
          onChange={handleNewValue}
          onFocus={handleFocus}
          onBlur={verifyValue}
          min={0}
          ref={inputRef}
          className={styles.mobile}
          data-cy="mobile-investment-input"
        />
        {
          (isOpen)
            ? (
              <input
                type="number"
                value={tmpValue || ''}
                onChange={handleNewValue}
                onFocus={handleFocus}
                onBlur={verifyValue}
                min={0}
                ref={inputRef}
                className={styles.desktop}
                data-cy="type-investment-input"
              />
            )
            : (
              <input
                type="text"
                value={tmpValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                onFocus={handleFocus}
                readOnly
                className={styles.desktop}
                data-cy="readable-investment-input"
              />
            )
        }
        {
          (!isOpen && (tmpValue !== value))
          && <Warning className={styles.warning} />
        }
        {
          (isOpen && shares.length > 0)
          && (
            <div
              className={`${styles.dropdown}`}
              ref={dropdownRef}
            >
              <p className={styles.selection}>
                {t('investments.amount.desired')}
              </p>
              {
                shares.map((share) => (
                  <button
                    type="button"
                    name={share.toFixed(2)}
                    key={share}
                    onClick={handleValueSelection}
                    className={styles.button}
                    data-cy="investment-dropdown-button"
                  >
                    {share.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    <span className={styles.euro}>
                      €
                    </span>
                  </button>
                ))
              }
            </div>
          )
        }
      </div>
      {
        (!isOpen && (tmpValue !== value))
        && <p className={`${styles.hint} ${styles.error}`}>{t('investments.amount.invalid')}</p>
      }
    </div>
  );
}
